import { useEffect, useRef } from 'react';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
// Narrow import: the event bus alone, not the full context barrel.
import { EventType, useEventSubscription } from 'context/EventBusContext';
import { useGetCaptableInfo } from 'services/hooks/captable';

/**
 * Re-fetches the cap table whenever a generation job finishes applying its
 * results (Process and Reprocess publish the same completion event). This is
 * the single subscriber for that event, and it belongs on the cap-table page
 * root so the refresh happens no matter which drawer tab is active, whether
 * the drawer is open, or whether it is popped out to its own window.
 */
const useCapTableGenerationRefresh = () => {
  const [{ captableInfo }] = useStore() as unknown as UseStoreValues;
  const [, getCapTableInfo] = useGetCaptableInfo();
  const refetch = getCapTableInfo as (id?: number) => Promise<void>;
  // A completion can land while the cap table is still loading (cold reload:
  // the resume apply can finish before getCapTableInfo sets the id). Remember
  // it and refresh as soon as the id arrives, so the grid never stays stale.
  const pendingRefreshRef = useRef(false);

  useEventSubscription(EventType.CAP_TABLE_GENERATION_JOB_COMPLETED, () => {
    if (captableInfo?.id) {
      refetch(captableInfo.id);
    } else {
      pendingRefreshRef.current = true;
    }
  });

  useEffect(() => {
    if (pendingRefreshRef.current && captableInfo?.id) {
      pendingRefreshRef.current = false;
      refetch(captableInfo.id);
    }
    // refetch reads its state from the hook closure; keying on it would rebind
    // the effect every render without changing behavior (the flag gates it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captableInfo?.id]);
};

export default useCapTableGenerationRefresh;
